import { useCallback, useMemo, useState } from 'react';

import { readUserInputMessage } from '../../lib/helpers/graphql-error.helpers';
import { useIntlSnackbar } from '../../lib/hooks/use-intl-snackbar';
import { useOperationErrorHandler } from '../../lib/hooks/use-operation-error-handler';
import { buildSetAgeGroupReportCardAccess } from '../../lib/services/report-card-access.service';
import { OUTCOME_SNACKBAR_MS } from '../../lib/snackbar.constants';
import { useCompanySportTerminology } from '../../lib/stores/company.store';
import { ageGroupAccessCopy } from './report-card-access.helpers';
import { AgeGroupAccessWrite } from './report-card-access.types';
import { getMessages } from './report-card-availability.messages';
import { useAgeGroupWriteFlight } from './report-card-age-group.use-write-flight';

// Revoke or re-enable a whole age group's report card access: confirmed first from the panel, or run
// straight from the send dialog, whose body already states the consequence. Reports its outcome.
export const useAgeGroupReportCardAccess = (ageGroup: IAgeGroup, availability?: IReportCardAvailability) => {
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);
  const copy = useMemo(() => ageGroupAccessCopy(messages), [messages]);
  const showSnackbar = useIntlSnackbar();
  const reportFailure = useOperationErrorHandler(messages.errRevokeFailed);
  const { setAgeGroupReportCardAccess } = buildSetAgeGroupReportCardAccess();
  const [confirming, setConfirming] = useState<AgeGroupAccessWrite | null>(null);

  const write = useCallback(async (action: AgeGroupAccessWrite) => {
    if (!availability) return;
    const values = { ageGroup: ageGroup.name };
    try {
      await setAgeGroupReportCardAccess(ageGroup, availability, action === 'RE_ENABLE');
      showSnackbar(copy[action].done, 'success', OUTCOME_SNACKBAR_MS, values);
    } catch (error) {
      const reason = readUserInputMessage(error);
      reportFailure(`report card age group ${action}`, error, {
        message: reason ? copy[action].refused : copy[action].failed,
        values: { ...values, reason },
      });
    }
  }, [ageGroup, availability, copy, showSnackbar, reportFailure, setAgeGroupReportCardAccess]);

  const { run, inFlight, busy } = useAgeGroupWriteFlight(ageGroup._id, 'ACCESS', write);

  const request = useCallback((action: AgeGroupAccessWrite) => setConfirming(action), []);
  const cancel = useCallback(() => setConfirming(null), []);
  const confirm = useCallback(() => {
    if (!confirming) return;
    setConfirming(null);
    run(confirming);
  }, [confirming, run]);
  const reEnable = useCallback(() => run('RE_ENABLE'), [run]);

  return { messages, confirming, request, confirm, cancel, reEnable, inFlight, busy };
};
